
import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Bot, Loader2, Sparkles, BookOpen, Database, AlertCircle, Search, Mic, Check, Eye, ExternalLink } from 'lucide-react';
import { ChatMessage, Language, SemanticSearchResult } from '../types';
import { studyCompanionChat, transcribeAndRefineAudio } from '../services/geminiService';
import { collection, getDocs, limit, query, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { useSearchIndex, runSearchIndex, refreshSearchIndex } from '../services/searchIndexStore';

function renderChatText(text: string): string {
  if (!text) return '';
  const mathSegs: string[] = [];
  let s = text;
  const extractMath = (re: RegExp, display: boolean) => {
    s = s.replace(re, (_, tex) => {
      try {
        // @ts-ignore
        if (typeof window !== 'undefined' && window.katex) {
          // @ts-ignore
          const html = window.katex.renderToString(tex.trim(), { displayMode: display, throwOnError: false });
          const id = `\x00M${mathSegs.length}\x00`;
          mathSegs.push(html);
          return id;
        }
      } catch {}
      return display ? `$$${tex}$$` : `$${tex}$`;
    });
  };
  extractMath(/\$\$([\s\S]*?)\$\$/g, true);
  extractMath(/\$([^\s$\n][^$\n]*?[^\s$\n]|[^\s$\n])\$/g, false);
  // Escape HTML in plain-text portions, then restore math and formatting
  s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
  // Use <br> only — avoids block-element-inside-<p> which breaks flex layout
  s = s.replace(/\n\n+/g, '<br/><br/>').replace(/\n/g, '<br/>');
  mathSegs.forEach((html, i) => { s = s.replace(`\x00M${i}\x00`, html); });
  return s;
}

interface ChatWidgetProps {
  language: Language;
  rightOffset?: number;
  onNavigateToNote: (notebookId: string, nodeId: string) => void;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ language, rightOffset = 24, onNavigateToNote }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Indexing state is shared (services/searchIndexStore): a run started from a notebook's menu
  // shows its progress here too.
  const searchIndex = useSearchIndex();
  const isBuilding = searchIndex.scanning || !!searchIndex.running;
  const indexPhase: 'idle' | 'scanning' | 'indexing' = searchIndex.running ? 'indexing' : searchIndex.scanning ? 'scanning' : 'idle';
  const indexProgress = searchIndex.running ? { current: searchIndex.running.current, total: searchIndex.running.total } : null;
  const [indexStatus, setIndexStatus] = useState<'unknown' | 'empty' | 'ready'>('unknown');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [previewSource, setPreviewSource] = useState<SemanticSearchResult | null>(null);
  const [previewData, setPreviewData] = useState<{ content: string; imageBase64?: string | null; sourceExtractedText?: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeStreamRef = useRef<MediaStream | null>(null);
  const previewCacheRef = useRef<Map<string, { content: string; imageBase64?: string | null; sourceExtractedText?: string }>>(new Map());
  const previewHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const handleMicStart = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      activeStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        activeStreamRef.current = null;
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setIsTranscribing(true);
        try {
          const reader = new FileReader();
          reader.readAsDataURL(audioBlob);
          reader.onloadend = async () => {
            const base64 = (reader.result as string).split(',')[1];
            try {
              const text = await transcribeAndRefineAudio(base64, language);
              if (text) setInputValue(text);
            } catch { /* ignore */ } finally {
              setIsTranscribing(false);
            }
          };
          reader.onerror = () => setIsTranscribing(false);
        } catch {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => setRecordingDuration(d => d + 1), 1000);
    } catch (err) {
      console.error('Mic error', err);
    }
  };

  const handleMicStop = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    stopTimer();
  };

  const handleMicCancel = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      if (mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
    }
    activeStreamRef.current?.getTracks().forEach(t => t.stop());
    activeStreamRef.current = null;
    audioChunksRef.current = [];
    setIsRecording(false);
    stopTimer();
  };

  const cancelPreviewHide = () => {
    if (previewHideTimerRef.current) { clearTimeout(previewHideTimerRef.current); previewHideTimerRef.current = null; }
  };

  const schedulePreviewHide = (delay = 200) => {
    cancelPreviewHide();
    previewHideTimerRef.current = setTimeout(() => { setPreviewSource(null); setPreviewData(null); }, delay);
  };

  const handleSourceEyeEnter = async (source: SemanticSearchResult) => {
    cancelPreviewHide();
    setPreviewSource(source);
    const cacheKey = `${source.notebookId}/${source.nodeId}`;
    if (previewCacheRef.current.has(cacheKey)) {
      setPreviewData(previewCacheRef.current.get(cacheKey)!);
      return;
    }
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const snap = await getDoc(doc(db, `users/${uid}/notebooks/${source.notebookId}/nodes/${source.nodeId}`));
      if (snap.exists()) {
        const d = snap.data();
        const result = { content: d.content || '', sourceExtractedText: d.sourceExtractedText || '', imageBase64: d.imageBase64 || null };
        previewCacheRef.current.set(cacheKey, result);
        setPreviewData(result);
      }
    } catch { /* ignore */ } finally {
      setPreviewLoading(false);
    }
  };

  const introText = language === 'vietnamese'
    ? 'Xin chào! Tôi là trợ lý học tập của bạn. Hỏi tôi bất cứ điều gì — tôi sẽ tìm kiếm trong ghi chú của bạn và trả lời dựa trên những gì bạn đã học.\n\nVí dụ: "note nào chứng minh MLE là CRLB?" hoặc "giải thích lại gradient descent cho tôi"'
    : "Hi! I'm your study companion. Ask me anything — I'll search your notes and answer based on what you've already studied.\n\nExample: \"which note proves MLE = CRLB?\" or \"explain gradient descent to me\"";

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ id: 'intro', role: 'model', text: introText, timestamp: Date.now() }]);
    }
    if (isOpen) void refreshSearchIndex(); // how many notes the assistant cannot see yet
    if (isOpen && indexStatus === 'unknown') {
      const uid = auth.currentUser?.uid;
      if (uid) {
        getDocs(query(collection(db, `users/${uid}/searchIndex`), limit(1)))
          .then(snap => setIndexStatus(snap.empty ? 'empty' : 'ready'))
          .catch(() => {});
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  const handleBuildIndex = async () => {
    try {
      // The shared store scans, indexes what is missing (resuming an interrupted run) and
      // publishes progress to every surface that shows it.
      const { indexed, totalNotes } = await runSearchIndex();
      setIndexStatus('ready');
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'model',
        text: indexed === 0
          ? (language === 'vietnamese'
            ? `✅ Tất cả ${totalNotes} ghi chú đã được lập chỉ mục và cập nhật.`
            : `✅ All ${totalNotes} notes are already indexed and up to date.`)
          : (language === 'vietnamese'
            ? `✅ Hoàn tất! Đã lập chỉ mục ${indexed} ghi chú mới. Tổng ${totalNotes} ghi chú có thể tìm kiếm.`
            : `✅ Done! Indexed ${indexed} new notes. ${totalNotes} total notes are now searchable.`),
        timestamp: Date.now()
      }]);
    } catch {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'model',
        text: language === 'vietnamese' ? '❌ Lập chỉ mục thất bại. Thử lại sau.' : '❌ Indexing failed. Please try again.',
        timestamp: Date.now(),
        isError: true
      }]);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userText = inputValue.trim();
    setInputValue('');

    const userMsg: ChatMessage = { id: Date.now().toString(), role: 'user', text: userText, timestamp: Date.now() };
    const newHistory = [...messages, userMsg];
    setMessages(newHistory);
    setIsLoading(true);

    const historyForApi = messages
      .filter(m => m.id !== 'intro')
      .map(m => ({ role: m.role, text: m.text }));

    try {
      const result = await studyCompanionChat(userText, historyForApi);

      if (result.sources.length > 0) setIndexStatus('ready');
      else if (indexStatus === 'unknown') setIndexStatus('empty');

      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'model',
        text: result.answer,
        timestamp: Date.now(),
        sources: result.sources.filter(s => !s.lowConfidence),
        totalSearched: result.totalSearched,
      }]);
    } catch (error: any) {
      const isRateLimit = error?.code === 'resource-exhausted' || error?.message?.includes('rate_limit');
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'model',
        text: isRateLimit
          ? (language === 'vietnamese' ? '⚠️ Quá nhiều yêu cầu. Vui lòng thử lại sau vài giây.' : '⚠️ Rate limited. Please wait a moment and try again.')
          : (language === 'vietnamese' ? '❌ Đã xảy ra lỗi. Thử lại sau.' : '❌ Something went wrong. Please try again.'),
        timestamp: Date.now(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const style = { right: `${rightOffset}px` };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={style}
        className="fixed bottom-6 p-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-full shadow-2xl transition-all hover:scale-110 z-50 flex items-center justify-center duration-300"
        title={language === 'vietnamese' ? 'Trợ lý học tập AI' : 'AI Study Companion'}
      >
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse border-2 border-white"></div>
        <Bot size={26} />
      </button>
    );
  }

  return (
  <>
    <div
      style={{ ...style, display: 'grid', gridTemplateRows: 'auto 1fr auto' }}
      className="fixed bottom-6 w-[400px] h-[620px] max-h-[85vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border dark:border-slate-700 overflow-hidden z-50 animate-in slide-in-from-bottom-10 fade-in duration-300"
    >
      {/* Row 1: Header */}
      <div className="p-4 bg-gradient-to-r from-violet-600 to-indigo-600 text-white flex justify-between items-center">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-white/20 rounded-lg"><Bot size={18} /></div>
          <div>
            <h3 className="font-bold text-sm">{language === 'vietnamese' ? 'Trợ lý học tập' : 'Study Companion'}</h3>
            <p className="text-[10px] opacity-75 flex items-center gap-1">
              <BookOpen size={9} />
              {language === 'vietnamese' ? 'Tìm kiếm & giải thích từ ghi chú của bạn' : 'Search & explain from your notes'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors ml-1">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Row 2: Middle (fills remaining space) — banners + messages together */}
      <div style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Build index progress */}
        {isBuilding && (
          <div style={{ flexShrink: 0 }} className="px-4 py-2.5 bg-violet-50 dark:bg-violet-900/20 border-b dark:border-slate-700">
            {indexPhase === 'scanning' ? (
              <div className="flex items-center gap-2 text-[11px] font-medium text-violet-700 dark:text-violet-300">
                <Search size={11} className="animate-pulse shrink-0" />
                <span>{language === 'vietnamese' ? 'Đang quét tất cả notebooks...' : 'Scanning all notebooks...'}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-1.5 text-[11px] font-medium text-violet-700 dark:text-violet-300">
                  <span>
                    {indexProgress
                      ? (language === 'vietnamese'
                          ? `Đang lập chỉ mục ${indexProgress.current} / ${indexProgress.total} ghi chú...`
                          : `Indexing ${indexProgress.current} / ${indexProgress.total} notes...`)
                      : (language === 'vietnamese' ? 'Đang khởi động...' : 'Starting...')}
                  </span>
                  <span className="tabular-nums">
                    {indexProgress && indexProgress.total > 0
                      ? `${Math.round((indexProgress.current / indexProgress.total) * 100)}%`
                      : '0%'}
                  </span>
                </div>
                <div className="h-1.5 bg-violet-200 dark:bg-violet-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-violet-500 rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: indexProgress && indexProgress.total > 0
                        ? `${Math.round((indexProgress.current / indexProgress.total) * 100)}%`
                        : '0%'
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Index missing or behind: one quiet line with the action, instead of a permanent header button */}
        {!isBuilding && (indexStatus === 'empty' || searchIndex.needsIndex > 0) && (
          <div style={{ flexShrink: 0 }} className="mx-3 mt-2 px-2.5 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertCircle size={13} className="shrink-0" />
            <span className="flex-1">
              {searchIndex.needsIndex > 0
                ? (language === 'vietnamese'
                  ? `${searchIndex.needsIndex} ghi chú chưa được lập chỉ mục — AI chưa tìm được chúng.`
                  : `${searchIndex.needsIndex} notes are not indexed yet — AI cannot find them.`)
                : (language === 'vietnamese'
                  ? 'Chưa có chỉ mục — AI chưa tìm được trong ghi chú của bạn.'
                  : 'No index yet — AI cannot search your notes.')}
            </span>
            <button onClick={handleBuildIndex} className="shrink-0 font-semibold underline underline-offset-2 hover:no-underline">
              {language === 'vietnamese' ? 'Cập nhật' : 'Update'}
            </button>
          </div>
        )}

        {/* Messages */}
        <div ref={messagesContainerRef} style={{ flex: '1 1 0%', minHeight: 0, overflowY: 'auto' }} className="p-4 space-y-4 bg-gray-50 dark:bg-slate-950">
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[88%] flex flex-col gap-2`}>
              <div className={`rounded-2xl p-3 text-sm leading-relaxed shadow-sm ${
                msg.role === 'user'
                  ? 'bg-violet-600 text-white rounded-br-none'
                  : msg.isError
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-bl-none'
                    : 'bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 border border-gray-100 dark:border-slate-700 rounded-bl-none'
              }`}>
                {msg.role === 'model' && !msg.isError && (
                  <div className="flex items-center gap-1.5 mb-1.5 text-[10px] font-bold text-violet-500 dark:text-violet-400 uppercase tracking-wider">
                    <Sparkles size={9} /> AI
                  </div>
                )}
                {msg.role === 'user' || msg.isError
                  ? <div className="whitespace-pre-wrap">{msg.text}</div>
                  : <div className="text-sm leading-relaxed [&_.katex]:text-[0.9em] [&_.katex-display]:my-2 [&_.katex-display]:overflow-x-auto [&_p]:mb-2 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: renderChatText(msg.text) }} />
                }
              </div>

              {/* Source notes */}
              {msg.sources && msg.sources.length > 0 && (
                <div className="flex flex-col gap-1.5 pl-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-wider">
                      {language === 'vietnamese' ? 'Ghi chú liên quan' : 'From your notes'}
                    </span>
                    {msg.totalSearched != null && msg.totalSearched > 0 && (
                      <span className="text-[9px] text-violet-400 dark:text-violet-500 flex items-center gap-0.5">
                        <Search size={8} />
                        {language === 'vietnamese'
                          ? `tìm trong ${msg.totalSearched} ghi chú`
                          : `searched ${msg.totalSearched} notes`}
                      </span>
                    )}
                  </div>
                  {msg.sources.map((s, idx) => {
                    const pct = Math.round(s.similarity * 100);
                    const isTop = idx === 0;
                    const isThisPreviewed = previewSource?.nodeId === s.nodeId && previewSource?.notebookId === s.notebookId;
                    return (
                      <div
                        key={s.nodeId}
                        className={`flex items-start gap-1.5 p-2 bg-white dark:bg-slate-800 border rounded-xl transition-colors group ${
                          isTop
                            ? 'border-violet-300 dark:border-violet-600 bg-violet-50/50 dark:bg-violet-900/20'
                            : 'border-gray-200 dark:border-slate-700 hover:border-violet-400 dark:hover:border-violet-500'
                        } ${isThisPreviewed ? 'ring-1 ring-violet-400 dark:ring-violet-500' : ''}`}
                      >
                        {/* Eye icon — hover triggers preview */}
                        <span
                          className={`shrink-0 mt-0.5 p-0.5 rounded transition-colors cursor-default ${isThisPreviewed ? 'text-violet-600 dark:text-violet-400' : 'text-gray-300 dark:text-gray-600 group-hover:text-violet-400 dark:group-hover:text-violet-500'}`}
                          onMouseEnter={() => handleSourceEyeEnter(s)}
                          onMouseLeave={() => schedulePreviewHide()}
                          title={language === 'vietnamese' ? 'Xem trước nội dung' : 'Preview note'}
                        >
                          <Eye size={13} />
                        </span>
                        {/* Clickable content */}
                        <button
                          onClick={() => { onNavigateToNote(s.notebookId, s.nodeId); setIsOpen(false); }}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="flex items-center gap-1.5">
                            <div className="text-xs font-semibold text-gray-800 dark:text-gray-200 group-hover:text-violet-600 dark:group-hover:text-violet-400 truncate flex-1">{s.nodeTitle}</div>
                            {isTop && (
                              <span className="shrink-0 text-[8px] font-bold text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/40 px-1 py-0.5 rounded">
                                {language === 'vietnamese' ? 'Phù hợp nhất' : 'Best match'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{s.notebookTitle}</div>
                            <div className="shrink-0 ml-2 flex items-center gap-1">
                              <div className="w-10 h-1 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full bg-violet-400 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-[9px] text-gray-400 dark:text-gray-500 tabular-nums">{pct}%</span>
                            </div>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white dark:bg-slate-800 p-3 rounded-2xl rounded-bl-none border border-gray-100 dark:border-slate-700 flex gap-1 items-center">
              <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce delay-75"></div>
              <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce delay-150"></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      </div>{/* end Row 2 */}

      {/* Row 3: Input */}
      <div className="p-3 bg-white dark:bg-slate-900 border-t dark:border-slate-700">
        <style>{`@keyframes barWave{0%,100%{transform:scaleY(0.3)}50%{transform:scaleY(1)}}`}</style>
        {isRecording ? (
          /* Recording UI */
          <div className="flex items-center gap-2.5 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200 dark:border-red-800">
            <div className="shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
              <Mic size={15} className="text-white" />
            </div>
            <div className="flex items-center gap-[2px] flex-1 h-7 overflow-hidden">
              {[35,70,55,85,45,90,60,80,40,75,65,50,88,42,72,58,82,48,78,52].map((h, i) => (
                <div
                  key={i}
                  className="w-[3px] bg-red-400 dark:bg-red-500 rounded-full origin-center"
                  style={{ height: `${h}%`, animation: 'barWave 1.2s ease-in-out infinite', animationDelay: `${i * 60}ms` }}
                />
              ))}
            </div>
            <div className="shrink-0 text-right">
              <div className="text-[10px] font-semibold text-red-500">{language === 'vietnamese' ? 'Đang nghe' : 'Listening'}</div>
              <div className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                {Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')}
              </div>
            </div>
            <button onClick={handleMicCancel} className="shrink-0 p-1.5 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-full text-gray-400 transition-colors" title="Hủy">
              <X size={15} />
            </button>
            <button onClick={handleMicStop} className="shrink-0 p-1.5 bg-violet-600 hover:bg-violet-700 rounded-full text-white transition-colors shadow-sm" title="Xong">
              <Check size={15} />
            </button>
          </div>
        ) : (
          /* Normal input */
          <div className="flex items-end gap-2">
            <button
              onClick={handleMicStart}
              disabled={isTranscribing || isLoading}
              className="shrink-0 p-2.5 rounded-xl text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-40"
              title={language === 'vietnamese' ? 'Nhập bằng giọng nói' : 'Voice input'}
            >
              {isTranscribing ? <Loader2 size={18} className="animate-spin text-violet-500" /> : <Mic size={18} />}
            </button>
            <textarea
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isTranscribing
                ? (language === 'vietnamese' ? 'Đang xử lý giọng nói...' : 'Transcribing...')
                : (language === 'vietnamese' ? 'Hỏi hoặc nói chuyện...' : 'Ask or chat...')}
              disabled={isTranscribing}
              className="flex-1 py-2.5 px-3 bg-gray-100 dark:bg-slate-800 border border-transparent focus:bg-white dark:focus:bg-slate-950 focus:border-violet-400 rounded-xl outline-none resize-none text-sm dark:text-white transition-all disabled:opacity-60"
              rows={1}
              style={{ minHeight: '44px', maxHeight: '128px' }}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading || isTranscribing}
              className="shrink-0 p-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        )}
      </div>
    </div>

    {/* Note preview panel — floats to the left of the chat widget */}
    {previewSource && (
      <div
        style={{ right: `${rightOffset + 416}px`, bottom: '24px' }}
        className="fixed w-72 max-h-[480px] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-violet-200 dark:border-violet-800 overflow-hidden z-50 flex flex-col"
        onMouseEnter={cancelPreviewHide}
        onMouseLeave={() => schedulePreviewHide(150)}
      >
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/30 dark:to-indigo-900/20 border-b border-violet-100 dark:border-violet-800">
          <div className="text-[10px] font-semibold text-violet-500 dark:text-violet-400 uppercase tracking-wider mb-0.5 truncate">{previewSource.notebookTitle}</div>
          <div className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">{previewSource.nodeTitle}</div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {previewLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={20} className="animate-spin text-violet-400" />
            </div>
          ) : previewData ? (
            <div className="space-y-3">
              {previewData.imageBase64 && (
                <img src={previewData.imageBase64} alt="" className="w-full rounded-lg object-cover max-h-40" />
              )}
              {(previewData.content || previewData.sourceExtractedText) ? (
                <p className="text-[13px] text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                  {previewData.content || previewData.sourceExtractedText}
                </p>
              ) : !previewData.imageBase64 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic text-center py-4">
                  {language === 'vietnamese' ? 'Ghi chú không có nội dung text' : 'No text content'}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-6">
              {language === 'vietnamese' ? 'Không tìm thấy nội dung' : 'Content not found'}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 flex items-center justify-between">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <Eye size={9} /> {language === 'vietnamese' ? 'Xem trước' : 'Preview'}
          </span>
          <button
            onClick={() => { onNavigateToNote(previewSource.notebookId, previewSource.nodeId); setIsOpen(false); }}
            className="flex items-center gap-1 text-[11px] font-semibold text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 transition-colors"
          >
            <ExternalLink size={11} />
            {language === 'vietnamese' ? 'Mở ghi chú' : 'Open note'}
          </button>
        </div>
      </div>
    )}
  </>
  );
};
